import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getClaimSummary,
  verifyClaimIdentity,
  acceptReferral,
  declineReferral,
} from "../APIs/externalReferralApi";

const URGENCY_LABELS = {
  ROUTINE: "Routine",
  URGENT: "Urgent",
  EMERGENT: "Emergent",
};

const URGENCY_COLORS = {
  ROUTINE: "bg-green-100 text-green-700",
  URGENT: "bg-yellow-100 text-yellow-700",
  EMERGENT: "bg-red-100 text-red-700",
};

export default function ClaimReferralPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  // step: "verify" | "details" | "done"
  const [step, setStep] = useState("verify");
  const [summary, setSummary] = useState(null); // specialty + urgency (non-PII)
  const [details, setDetails] = useState(null); // full verified data
  const [outcome, setOutcome] = useState(null); // "accepted" | "declined"

  const [npi, setNpi] = useState("");
  const [lastName, setLastName] = useState("");
  const [verifyError, setVerifyError] = useState("");
  const [verifying, setVerifying] = useState(false);

  const [showMore, setShowMore] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [declineError, setDeclineError] = useState("");
  const [actioning, setActioning] = useState(false);
  const [tokenError, setTokenError] = useState("");

  useEffect(() => {
    if (!token) {
      setTokenError("No referral token found. Please check your link.");
      return;
    }
    getClaimSummary(token)
      .then(setSummary)
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 410) setTokenError("This referral link has expired.");
        else if (status === 404)
          setTokenError("This referral link is invalid.");
        else setTokenError("Unable to load referral. Please try again.");
      });
  }, [token]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setVerifyError("");
    setVerifying(true);
    try {
      const data = await verifyClaimIdentity({
        token,
        npi: npi.trim(),
        lastName: lastName.trim(),
      });
      setDetails(data);
      setStep("details");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403)
        setVerifyError(
          "The NPI or last name you entered does not match our records.",
        );
      else if (status === 410)
        setVerifyError("This referral link has expired.");
      else setVerifyError("Something went wrong. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const handleAccept = async () => {
    setActioning(true);
    try {
      await acceptReferral({
        token,
        npi: npi.trim(),
        lastName: lastName.trim(),
      });
      setOutcome("accepted");
      setStep("done");
    } catch {
      setActioning(false);
    }
  };

  const handleDeclineSubmit = async () => {
    if (!declineReason.trim()) {
      setDeclineError("Please provide a reason for declining.");
      return;
    }
    setActioning(true);
    try {
      await declineReferral({
        token,
        npi: npi.trim(),
        lastName: lastName.trim(),
        reason: declineReason.trim(),
      });
      setOutcome("declined");
      setDeclineOpen(false);
      setStep("done");
    } catch {
      setDeclineError("Something went wrong. Please try again.");
      setActioning(false);
    }
  };

  // ── Error state ──────────────────────────────────────────────────────────
  if (tokenError) {
    return (
      <PageShell>
        <div className="text-center py-8">
          <p className="text-red-600 font-medium">{tokenError}</p>
          <p className="text-sm text-gray-500 mt-2">
            If you believe this is an error, contact{" "}
            <a
              href="mailto:support@medmazehealth.com"
              className="text-green-600 underline"
            >
              support@medmazehealth.com
            </a>
          </p>
        </div>
      </PageShell>
    );
  }

  // ── Done state ───────────────────────────────────────────────────────────
  if (step === "done") {
    return (
      <PageShell>
        <div className="text-center py-8">
          <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-7 h-7 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-800 mb-1">
            {outcome === "accepted" ? "Referral Accepted" : "Referral Declined"}
          </h2>
          <p className="text-sm text-gray-500">
            Thank you — the referring provider has been notified.
          </p>
        </div>
      </PageShell>
    );
  }

  // ── Step 1: Verify identity ──────────────────────────────────────────────
  if (step === "verify") {
    return (
      <PageShell>
        {/* Shield icon */}
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mb-3">
            <svg
              className="w-7 h-7 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.8}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
          </div>
          <h1 className="text-lg font-semibold text-gray-800">
            Secure Referral Access
          </h1>
          {summary && (
            <p className="text-sm text-gray-500 mt-1">
              {summary.specialty} referral ·{" "}
              <span
                className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${URGENCY_COLORS[summary.urgency] || "bg-gray-100 text-gray-600"}`}
              >
                {URGENCY_LABELS[summary.urgency] || summary.urgency}
              </span>
            </p>
          )}
        </div>

        <p className="text-sm text-gray-500 mb-5 text-center">
          To view this referral, please verify your identity using your NPI
          number and last name.
        </p>

        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              NPI Number
            </label>
            <input
              className="w-full border rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
              placeholder="10-digit NPI"
              value={npi}
              onChange={(e) => setNpi(e.target.value)}
              maxLength={10}
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Last Name
            </label>
            <input
              className="w-full border rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
              placeholder="As it appears in NPI registry"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
          {verifyError && <p className="text-red-600 text-sm">{verifyError}</p>}
          <button
            type="submit"
            disabled={verifying || !npi.trim() || !lastName.trim()}
            className="w-full py-3 rounded-lg bg-green-600 text-white font-semibold text-sm disabled:opacity-40 hover:bg-green-700 transition"
          >
            {verifying ? "Verifying..." : "Continue"}
          </button>
        </form>

        <p className="text-xs text-gray-400 text-center mt-6">
          Need help?{" "}
          <a
            href="mailto:support@medmazehealth.com"
            className="text-green-600 underline"
          >
            Contact Support
          </a>
        </p>
      </PageShell>
    );
  }

  // ── Step 2: Referral details ─────────────────────────────────────────────
  const d = details;
  const initials =
    [d?.patientFirstName?.[0], d?.patientLastName?.[0]]
      .filter(Boolean)
      .join("")
      .toUpperCase() || "?";
  const fullName = [d?.patientFirstName, d?.patientLastName]
    .filter(Boolean)
    .join(" ");
  const reasonLines = d?.reason ? d.reason.split(" | ") : [];
  const TRUNCATE_AT = 3;
  const visibleLines = showMore
    ? reasonLines
    : reasonLines.slice(0, TRUNCATE_AT);

  return (
    <>
      <PageShell wide>
        {/* Patient card */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-full bg-green-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
            {initials}
          </div>
          <div>
            <p className="font-semibold text-gray-800 text-base">
              {fullName || "Patient"}
            </p>
            {d?.patientDateOfBirth && (
              <p className="text-sm text-gray-500">
                DOB: {d.patientDateOfBirth}
              </p>
            )}
            {d?.patientPhone && (
              <p className="text-sm text-gray-500">{d.patientPhone}</p>
            )}
          </div>
        </div>

        {/* 3-column strip */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <InfoCard label="Specialty" value={d?.specialty || "—"} />
          <InfoCard
            label="Urgency"
            value={
              <span
                className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${URGENCY_COLORS[d?.urgency] || "bg-gray-100 text-gray-600"}`}
              >
                {URGENCY_LABELS[d?.urgency] || d?.urgency || "—"}
              </span>
            }
          />
          <InfoCard
            label="Referred By"
            value={
              d?.referringProviderName ? `Dr. ${d.referringProviderName}` : "—"
            }
          />
        </div>

        {/* Insurance */}
        {d?.insurancePlan && (
          <div className="mb-5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              Insurance
            </p>
            <p className="text-sm text-gray-700">{d.insurancePlan}</p>
          </div>
        )}

        {/* Clinical reason */}
        {reasonLines.length > 0 && (
          <div className="mb-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Reason for Referral
            </p>
            <ul className="space-y-1">
              {visibleLines.map((line, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm text-gray-700"
                >
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                  {line}
                </li>
              ))}
            </ul>
            {reasonLines.length > TRUNCATE_AT && (
              <button
                onClick={() => setShowMore((v) => !v)}
                className="text-xs text-green-600 mt-2 hover:underline"
              >
                {showMore
                  ? "Show less"
                  : `Show ${reasonLines.length - TRUNCATE_AT} more`}
              </button>
            )}
          </div>
        )}

        {/* Documents */}
        {d?.documents?.length > 0 && (
          <div className="mb-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Attached Documents ({d.documents.length})
            </p>
            <ul className="space-y-1">
              {d.documents.map((doc) => (
                <li
                  key={doc.documentId}
                  className="flex items-center gap-2 text-sm text-gray-700"
                >
                  <svg
                    className="w-4 h-4 text-gray-400 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.8}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  {doc.fileName}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* CTAs */}
        <div className="flex gap-3 pt-2">
          <button
            onClick={() => setDeclineOpen(true)}
            disabled={actioning}
            className="flex-1 py-3 rounded-lg border border-gray-300 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition disabled:opacity-40"
          >
            Decline Referral
          </button>
          <button
            onClick={handleAccept}
            disabled={actioning}
            className="flex-1 py-3 rounded-lg bg-green-600 text-white font-semibold text-sm hover:bg-green-700 transition disabled:opacity-40"
          >
            {actioning ? "Processing..." : "Accept Referral"}
          </button>
        </div>
      </PageShell>

      {/* Decline modal */}
      {declineOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
            <h2 className="text-base font-semibold text-gray-800 mb-1">
              Decline Referral
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Please provide a reason so the referring provider can follow up
              appropriately.
            </p>
            <textarea
              className="w-full border rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
              rows={4}
              placeholder="e.g. Outside of my scope of practice, patient should see a different specialist..."
              value={declineReason}
              onChange={(e) => {
                setDeclineReason(e.target.value);
                setDeclineError("");
              }}
            />
            {declineError && (
              <p className="text-red-600 text-sm mt-1">{declineError}</p>
            )}
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => {
                  setDeclineOpen(false);
                  setDeclineReason("");
                  setDeclineError("");
                }}
                disabled={actioning}
                className="flex-1 py-3 rounded-lg border border-gray-300 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDeclineSubmit}
                disabled={actioning || !declineReason.trim()}
                className="flex-1 py-3 rounded-lg bg-red-600 text-white font-semibold text-sm hover:bg-red-700 transition disabled:opacity-40"
              >
                {actioning ? "Declining..." : "Confirm Decline"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function PageShell({ children, wide }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-start sm:items-center justify-center px-4 py-10">
      <div
        className={`bg-white rounded-xl shadow-md w-full p-8 ${wide ? "max-w-lg" : "max-w-md"}`}
      >
        <div className="text-center mb-6">
          <p className="text-2xl font-bold text-green-600">MedMaze Health</p>
          <p className="text-sm text-gray-500 mt-1">
            Referral Management Platform
          </p>
        </div>
        {children}
      </div>
    </div>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3 text-center">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-sm font-medium text-gray-800">{value}</p>
    </div>
  );
}
